import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin, Search, Crosshair } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fieldInputCls } from "@/components/Field";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { TrackDialog } from "@/components/TrackDialog";
import { PageHeader } from "@/pages/Events";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { useTracks } from "@/lib/useTracks";

const TrackCard = ({ tr, onEdit, onDelete }) => {
  const { t } = useT();
  return (
    <div data-testid={`track-item-${tr.id}`} className="fade-up flex items-start gap-3 bg-[#12141C] border border-[#282D3D] rounded-sm p-4 hover:border-slate-600 transition-colors duration-200">
      <MapPin className="w-5 h-5 text-[#FF3B30] mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div data-testid="track-item-name" className="font-heading font-bold uppercase text-xl leading-tight truncate">{tr.name}</div>
        <div className="font-mono text-xs text-slate-400 mt-1">{[tr.city, tr.country].filter(Boolean).join(" · ") || "—"}</div>
        <div className="font-mono text-[11px] text-slate-500 mt-1">{tr.lat != null ? `${tr.lat.toFixed(4)}, ${tr.lon.toFixed(4)}` : t("no_coords")}</div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {tr.align && <span data-testid="track-aligned-badge" className="inline-flex items-center gap-1 px-1.5 py-0.5 font-mono text-[10px] uppercase border border-[#10B981] text-[#10B981] rounded-sm"><Crosshair className="w-3 h-3" />{t("aligned")}</span>}
          {(tr.aliases || []).map((a) => <span key={a} className="px-1.5 py-0.5 font-mono text-[10px] border border-[#282D3D] text-slate-400 rounded-sm">{a}</span>)}
        </div>
      </div>
      <button data-testid={`track-edit-${tr.id}`} onClick={() => onEdit(tr)} className="p-2 text-slate-500 hover:text-white rounded-sm hover:bg-[#282D3D] transition-colors duration-200"><Pencil className="w-4 h-4" /></button>
      <ConfirmDelete testid={`track-delete-${tr.id}`} onConfirm={() => onDelete(tr)} trigger={
        <button data-testid={`track-delete-${tr.id}-btn`} className="p-2 text-slate-500 hover:text-[#FF3B30] rounded-sm hover:bg-[#282D3D] transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
      } />
    </div>
  );
};

export default function Tracks() {
  const { t } = useT();
  const { tracks, reload } = useTracks();
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState({ open: false, item: null });
  const ql = q.toLowerCase();
  const shown = tracks.filter((tr) => [tr.name, tr.city, tr.country, ...(tr.aliases || [])].join(" ").toLowerCase().includes(ql));
  const onDelete = async (tr) => {
    await api.delete(`/tracks/${tr.id}`);
    toast.success(t("deleted"));
    reload();
  };
  return (
    <div>
      <PageHeader title={t("tracks_title")} sub={t("tracks_sub")} image="/images/race.jpg" action={
        <Button data-testid="track-add-btn" onClick={() => setDialog({ open: true, item: null })} className="rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase tracking-wide text-lg h-11 px-5">
          <Plus className="w-4 h-4 mr-1" /> {t("new_track")}
        </Button>
      } />
      <div className="relative max-w-md mb-6">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <Input data-testid="track-search-input" placeholder={t("search_track")} value={q} onChange={(e) => setQ(e.target.value)} className={`${fieldInputCls} pl-9`} />
      </div>
      <div data-testid="tracks-list" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {shown.map((tr) => <TrackCard key={tr.id} tr={tr} onEdit={(item) => setDialog({ open: true, item })} onDelete={onDelete} />)}
        {shown.length === 0 && <div data-testid="tracks-empty" className="border border-dashed border-[#282D3D] rounded-sm p-8 text-center text-slate-500 font-mono text-sm md:col-span-2 xl:col-span-3">{t("no_items")}</div>}
      </div>
      <TrackDialog open={dialog.open} item={dialog.item} onSaved={reload} onOpenChange={(o) => setDialog({ ...dialog, open: o })} />
    </div>
  );
}
